import {useCallback, useEffect, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useQuery} from '@apollo/client';
import {setExchangeScope} from 'redux/store/exchangeScope';
import {GET_EXCHANGE_SUMMARY, buildTotalsQuery, readTotals} from './resolvers';

/**
 * The four instrument types an exchange can list, in the order the tabs show them.
 *
 * `getFinancialInstruments` requires both keys, so a type is always a pair.
 */
export const INSTRUMENT_TYPES = [
  {
    key: 'PUBLIC_EQUITY',
    label: 'Equities',
    assetClassKey: 'EQUITIES',
    instrumentKey: 'PUBLIC_EQUITY',
  },
  {
    key: 'BOND',
    label: 'Bonds',
    assetClassKey: 'FIXED_INCOMES',
    instrumentKey: 'BOND',
  },
  {
    key: 'MUTUAL_FUND',
    label: 'Funds',
    assetClassKey: 'FUNDS',
    instrumentKey: 'MUTUAL_FUND',
  },
  {key: 'IPO', label: 'IPOs', assetClassKey: 'EQUITIES', instrumentKey: 'IPO'},
];

const CHEAP_INPUT = {
  includeSectors: false,
  includeCompositeIndex: false,
  moversLimit: 0,
};

/**
 * Reads the exchange lens and the list behind it.
 *
 * The URL is the source of truth so a link is shareable; the store mirrors it
 * so the scope survives a move between Multi Asset and Trading View, where the
 * URL changes entirely.
 */
export const useExchangeScope = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const stored = useSelector(state => state?.exchangeScope?.exchangeId ?? null);

  const fromUrl = searchParams.get('exchange');
  const exchangeId = fromUrl || stored || null;

  // Adopt a scope arriving by URL, and write a remembered one back into a URL
  // that does not carry it yet.
  useEffect(() => {
    if (fromUrl && fromUrl !== stored) {
      dispatch(setExchangeScope(fromUrl));
    } else if (!fromUrl && stored) {
      const next = new URLSearchParams(searchParams);
      next.set('exchange', stored);
      setSearchParams(next, {replace: true});
    }
  }, [fromUrl, stored]);

  const setScope = useCallback(
    next => {
      dispatch(setExchangeScope(next));
      const params = new URLSearchParams(searchParams);
      if (next) params.set('exchange', next);
      else params.delete('exchange');
      setSearchParams(params, {replace: true});
    },
    [searchParams, setSearchParams, dispatch],
  );

  const {data, loading} = useQuery(GET_EXCHANGE_SUMMARY, {
    variables: {input: CHEAP_INPUT},
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const nodes = data?.getExchanges?.data ?? [];
  // The node with no exchange is All markets, not a broken exchange.
  const exchanges = useMemo(
    () => nodes.filter(node => node.exchange).map(node => node),
    [data],
  );
  const aggregate = nodes.find(node => !node.exchange) ?? null;

  const exchangeIds = useMemo(
    () => exchanges.map(node => node.exchange.id),
    [exchanges],
  );

  const totalsQuery = useMemo(
    () => buildTotalsQuery(exchangeIds, INSTRUMENT_TYPES),
    [exchangeIds.join('|')],
  );

  const {data: totalsData} = useQuery(totalsQuery, {
    skip: !exchangeIds.length,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const totals = useMemo(
    () =>
      totalsData ? readTotals(totalsData, exchangeIds, INSTRUMENT_TYPES) : null,
    [totalsData, exchangeIds.join('|')],
  );

  const active = exchanges.find(node => node.exchange.id === exchangeId);

  return {
    loading,
    exchangeId,
    setScope,
    exchanges,
    aggregate,
    totals,
    activeExchange: active?.exchange ?? null,
    activeLabel: active?.exchange?.code ?? 'All markets',
    // Live is ENABLED-only, straight from getExchanges; total is every status.
    liveCount: active ? active.instrumentCount : aggregate?.instrumentCount,
    totalCount: totals ? (active ? totals[exchangeId] : totals.all) : null,
  };
};

export default useExchangeScope;
